import type { ColumnType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import type { Capability } from '../capability/CapabilityContext';
import { EM_DASH, formatDate, formatDateTime, formatEntityRef } from '../formatters';

/**
 * The kind × behavior matrix. This module is why sorting and filtering are
 * DEFAULTS rather than per-column opt-ins — the repo's sort coverage ran from
 * six columns on one screen to zero on another because every column declared
 * its own comparator by hand.
 *
 * Pure functions only: no React, no antd components, no DOM.
 */

export type ColumnKind =
  | 'text'
  | 'date'
  | 'dateTime'
  | 'number'
  | 'enum'
  | 'entityRef'
  | 'actions';

export type DataColumn<T> = ColumnType<T> & {
  kind?: ColumnKind;
  /** 'auto' (default) derives from `kind`. false opts out. */
  sortable?: 'auto' | false | ColumnType<T>['sorter'];
  /** 'auto' (default) derives from `kind`. false opts out. */
  filterable?: 'auto' | false | { options: { label: string; value: string }[] };
  cap?: Capability;
  /**
   * Projects a record to the string this column sorts, filters and searches
   * on. Required when the stored field is not itself the value — e.g. a
   * Status column backed by a nullable `deactivatedAt` timestamp.
   *
   * Deliberately NOT named `valueOf`: that name collides with
   * `Object.prototype.valueOf`, present on every JS object, which would make
   * every column look "projected" whether or not one was actually supplied.
   */
  projectValue?: (record: T) => string;
};

const WIDTHS: Record<ColumnKind, number> = {
  text: 200,
  date: 140,
  dateTime: 180,
  number: 120,
  enum: 140,
  entityRef: 180,
  actions: 56,
};

/** Looks up the default column width for a `kind`. An undefined kind is treated as `text`. */
export function defaultWidthFor(kind: ColumnKind | undefined): number {
  return WIDTHS[kind ?? 'text'];
}

function rawValue<T>(col: DataColumn<T>, record: T): unknown {
  const di = col.dataIndex;
  if (typeof di === 'string' || typeof di === 'number') {
    return (record as Record<string, unknown>)[di as string];
  }
  return undefined;
}

/**
 * The single definition of "what this column is about". Sorting, column
 * filtering and the search box all route through here.
 *
 * Returns '' — never the em dash — for a missing value, so a filter for "—"
 * cannot match every empty cell. That includes an empty-string raw value
 * feeding an `entityRef` column: without the `v === ''` check below,
 * `formatEntityRef` would render it as the em dash and a filter for "—"
 * would then match that row, which is exactly the anti-pattern this
 * function exists to prevent.
 */
export function cellTextFor<T>(col: DataColumn<T>, record: T): string {
  if (col.projectValue) return col.projectValue(record);
  const v = rawValue(col, record);
  if (v === null || v === undefined || v === '') return '';
  if (col.kind === 'entityRef') {
    return formatEntityRef({ name: String(v) });
  }
  return String(v);
}

/**
 * A column's value is "missing" only when read from the raw cell — a
 * `projectValue` projection (e.g. Status derived from `deactivatedAt`)
 * always resolves to a real value, so it is never nullish.
 */
function isNullish<T>(col: DataColumn<T>, record: T): boolean {
  if (col.projectValue) return false;
  const v = rawValue(col, record);
  return v === null || v === undefined || v === '';
}

/**
 * Nulls-vs-non-null ordering for a sort comparator, direction-safe.
 *
 * antd's `useSorter` negates whatever the comparator returns for a
 * descending sort (`sortOrder === 'ascend' ? result : -result` — see
 * `node_modules/antd/es/table/hooks/useSorter.js`). A comparator that always
 * returns "nulls sort after non-null" would therefore have its nulls float
 * to the TOP once antd negates it for descend. Pre-negating here cancels
 * that out, so "nulls last" holds in both directions.
 *
 * Returns `null` — distinct from a genuine tie (`0`) — as a sentinel meaning
 * "neither side is nullish, fall through to the real comparison".
 */
function nullsLast<T>(
  col: DataColumn<T>,
  a: T,
  b: T,
  sortOrder?: SortOrder,
): number | null {
  const an = isNullish(col, a);
  const bn = isNullish(col, b);
  if (an && bn) return 0;
  if (!an && !bn) return null;
  const raw = an ? 1 : -1; // a nullish value sorts after a present one
  return sortOrder === 'descend' ? -raw : raw;
}

/**
 * Resolves the sort comparator for a column: an explicit comparator wins,
 * `sortable: false` or `kind: 'actions'` opts out, otherwise the comparator
 * is derived from `kind` — numeric for date/dateTime/number, `localeCompare`
 * otherwise. Nullish values sort last in both directions.
 */
export function comparatorFor<T>(
  col: DataColumn<T>,
): ColumnType<T>['sorter'] | undefined {
  if (col.sortable === false) return undefined;
  if (col.kind === 'actions') return undefined;
  if (col.sortable && col.sortable !== 'auto') return col.sortable;

  if (col.kind === 'date' || col.kind === 'dateTime') {
    return (a: T, b: T, sortOrder?: SortOrder) => {
      const n = nullsLast(col, a, b, sortOrder);
      if (n !== null) return n;
      return (
        Date.parse(cellTextFor(col, a)) - Date.parse(cellTextFor(col, b))
      );
    };
  }

  if (col.kind === 'number') {
    return (a: T, b: T, sortOrder?: SortOrder) => {
      const n = nullsLast(col, a, b, sortOrder);
      if (n !== null) return n;
      return Number(cellTextFor(col, a)) - Number(cellTextFor(col, b));
    };
  }

  return (a: T, b: T, sortOrder?: SortOrder) => {
    const n = nullsLast(col, a, b, sortOrder);
    if (n !== null) return n;
    return cellTextFor(col, a).localeCompare(cellTextFor(col, b), undefined, {
      sensitivity: 'base',
    });
  };
}

/**
 * Resolves the column filter for a column: `filterable: false` or
 * `kind` of `date`/`dateTime`/`number`/`actions` opts out (no default filter
 * for those kinds). `enum` yields a checkbox list matched by exact value —
 * options come from `filterable.options` when supplied, otherwise derived
 * from the distinct values in `rows` (booleans stringify to `'true'`/`'false'`
 * so they can be used as filter keys). `text`/`entityRef` yield a
 * case-insensitive substring match.
 */
export function filterConfigFor<T>(
  col: DataColumn<T>,
  rows: T[],
):
  | {
      filters: { text: string; value: string }[];
      onFilter: (value: React.Key | boolean, record: T) => boolean;
    }
  | undefined {
  if (col.filterable === false) return undefined;
  if (
    col.kind === 'actions' ||
    col.kind === 'date' ||
    col.kind === 'dateTime' ||
    col.kind === 'number'
  ) {
    return undefined;
  }

  const explicit =
    col.filterable && col.filterable !== 'auto' ? col.filterable.options : null;

  if (col.kind === 'enum') {
    const filters = explicit
      ? explicit.map((o) => ({ text: o.label, value: o.value }))
      : Array.from(new Set(rows.map((r) => cellTextFor(col, r))))
          .filter((v) => v !== '')
          .sort()
          .map((v) => ({ text: v, value: v }));
    return {
      filters,
      onFilter: (value, record) => cellTextFor(col, record) === String(value),
    };
  }

  // text + entityRef → contains match
  const filters = explicit
    ? explicit.map((o) => ({ text: o.label, value: o.value }))
    : [];
  return {
    filters,
    onFilter: (value, record) => {
      const text = cellTextFor(col, record);
      if (text === '') return false;
      return text.toLowerCase().includes(String(value).toLowerCase());
    },
  };
}

/**
 * Resolves the default cell renderer for a column. A caller-supplied
 * `render` always wins untouched — this must never wrap or override a
 * hand-written renderer. `enum` and `actions` have no library-provided
 * renderer (the caller owns those), so they resolve to `undefined`.
 */
export function rendererFor<T>(
  col: DataColumn<T>,
): ColumnType<T>['render'] | undefined {
  if (col.render) return col.render;
  if (col.kind === 'date') return (v: unknown) => formatDate(v as string);
  if (col.kind === 'dateTime') return (v: unknown) => formatDateTime(v as string);
  if (col.kind === 'entityRef') {
    return (v: unknown) => formatEntityRef({ name: v as string | null });
  }
  if (col.kind === 'enum' || col.kind === 'actions') return undefined;
  return (v: unknown) =>
    v === null || v === undefined || v === '' ? EM_DASH : String(v);
}
